// Point groups hold each point once: adding points to a group takes them out of the others (editui.ts
// outOfOtherGroups). tidyGroups brings groups made before that rule into line without changing what a
// group means: where one group holds (nearly) all of another, it becomes that one's parent, and each
// point then stays only in the most specific group that held it. A parent still includes it through its
// sub-groups (groupAndBelow), so what a group shows and selects doesn't change. Where two groups only
// partly overlap (neither holds the other), the shared points go to the smaller one.
import { type State, type Op, groupAndBelow } from './log.ts';
import { decodeSet, encodeSet } from './pidset.ts';

export type OpBody = Omit<Op, 'v' | 'parent' | 't'>;

/** Nest B under A when at least this share of B is in A. */
export const NEST = 0.95;

/** The ops (group.move, then group.save) that tidy a scan's groups; [] if they need nothing. */
export function tidyGroups(st: State, nest = NEST): OpBody[] {
  const names = [...st.groups.keys()];
  if (names.length < 2) return [];
  const N = st.N, own = new Map(names.map(g => {
    const m = new Uint8Array(N);
    for (const p of decodeSet(st.groups.get(g)!, N)) m[p] = 1;
    return [g, m] as const;
  }));
  // what each group holds now, its own points and those of the groups below it
  const deep = new Map(names.map(g => {
    const m = new Uint8Array(N);
    for (const h of groupAndBelow(st, g)) { const o = own.get(h); if (o) for (let p = 0; p < N; p++) m[p] |= o[p]; }
    let n = 0;
    for (const x of m) n += x;
    return [g, { m, n }] as const;
  }));
  // bigger first (ties by name), so a parent is always bigger than its child: no cycles
  const order = [...names].sort((a, b) => deep.get(b)!.n - deep.get(a)!.n || (a < b ? -1 : 1));
  const rank = new Map(order.map((g, i) => [g, i]));
  const parent = new Map<string, string | null>(names.map(g => [g, st.groupParent.get(g) ?? null]));
  const ops: OpBody[] = [];
  for (const b of order) {
    const B = deep.get(b)!;
    if (!B.n) continue;
    // the most specific group (smallest, after b in no order) that holds nearly all of b
    let best: string | null = null;
    for (const a of order) {
      if (a === b || rank.get(a)! > rank.get(b)! || isAbove(parent, b, a)) continue;   // (never under its own sub-group)
      const A = deep.get(a)!;
      let k = 0;
      for (let p = 0; p < N; p++) if (B.m[p] && A.m[p]) k++;
      if (k >= nest * B.n && (best === null || deep.get(a)!.n < deep.get(best)!.n)) best = a;
    }
    const cur = parent.get(b) ?? null;
    // keep a current parent that is already more specific (below the best one)
    if (best !== null && best !== cur && !(cur !== null && isAbove(parent, best, cur))) {
      parent.set(b, best);
      ops.push({ op: 'group.move', name: b, under: best } as OpBody);
    }
  }
  const depth = (g: string) => { let d = 0; for (let h = parent.get(g) ?? null; h !== null && d <= names.length; h = parent.get(h) ?? null) d++; return d; };
  // each point: in the deepest group that held it (ties: the smaller), and only there
  const out = new Map(names.map(g => [g, new Uint8Array(N)]));
  const holders: string[] = [];
  for (let p = 0; p < N; p++) {
    holders.length = 0;
    for (const g of names) if (own.get(g)![p]) holders.push(g);
    if (!holders.length) continue;
    let k = holders[0];
    for (const g of holders.slice(1)) {
      const dg = depth(g), dk = depth(k);
      if (dg > dk || (dg === dk && deep.get(g)!.n < deep.get(k)!.n)) k = g;
    }
    out.get(k)![p] = 1;
  }
  for (const g of names) {
    const a = own.get(g)!, b = out.get(g)!;
    let same = true;
    for (let p = 0; p < N && same; p++) if (a[p] !== b[p]) same = false;
    if (same) continue;
    const pids: number[] = [];
    for (let p = 0; p < N; p++) if (b[p]) pids.push(p);
    ops.push({ op: 'group.save', name: g, set: encodeSet(pids) } as OpBody);
  }
  return ops;
}

/** Is a above b (or b itself) in the parent map? */
function isAbove(parent: Map<string, string | null>, a: string, b: string) {
  for (let g: string | null = b, k = 0; g !== null && k <= parent.size; g = parent.get(g) ?? null, k++) if (g === a) return true;
  return false;
}
